import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, Percent, Wallet, Clock } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';

export default function CreditosListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const showLoading = useVisibleLoading(loading);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/creditos');
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar las líneas de crédito');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        eyebrow="Asociados"
        title="Líneas de crédito"
        description="Conoce las líneas de crédito disponibles para asociados. Consulta condiciones y solicita asesoría en nuestras oficinas."
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-10 flex justify-center">
          <Link
            to="/creditos/simulador"
            className="inline-flex items-center gap-2 rounded-lg border border-[#1acb12]/40 bg-brand-green px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-green-dark"
          >
            <Calculator className="h-4 w-4" aria-hidden />
            Simulador de crédito
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
        )}

        <div className={error ? 'mt-10' : ''}>
          {showLoading && (
            <BrandLoading variant="inline" tone="light" label="Cargando créditos…" className="min-h-[40vh]" />
          )}

          {!showLoading && !error && items.length === 0 && (
            <p className="text-center text-slate-600">No hay líneas de crédito publicadas por ahora.</p>
          )}

          {!showLoading && items.length > 0 && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((c, i) => (
              <ScrollReveal key={c.id} delayMs={Math.min(i, 8) * 40} className="min-w-0">
                <Link
                  to={`/creditos/${c.id}`}
                  className="group block rounded-2xl no-underline text-inherit outline-none focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  aria-label={`${c.titulo}: ver detalle`}
                >
                  <PremiumCard hover className="flex h-full cursor-pointer flex-col">
                    <div className="relative mb-5 aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
                      {c.imagen ? (
                        <img
                          src={assetUrl(c.imagen)}
                          alt=""
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs font-medium uppercase tracking-wider text-slate-500">
                          Sin imagen
                        </div>
                      )}
                      <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                        {c.categoria}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <h2 className="font-display text-xl font-semibold text-white">{c.titulo}</h2>
                      <p className="mt-2 line-clamp-3 flex-1 text-base leading-relaxed text-slate-400 sm:text-lg">{c.resumen}</p>
                      <ul className="mt-4 space-y-2 text-sm text-slate-400">
                        {c.tasa_interes ? (
                          <li className="flex items-center gap-2">
                            <Percent className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            <span>
                              <span className="font-semibold text-slate-500">Interés: </span>
                              {c.tasa_interes}
                            </span>
                          </li>
                        ) : null}
                        {c.monto_maximo ? (
                          <li className="flex items-center gap-2">
                            <Wallet className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            {c.monto_maximo}
                          </li>
                        ) : null}
                        {c.plazo ? (
                          <li className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-accent-lime" aria-hidden />
                            {c.plazo}
                          </li>
                        ) : null}
                      </ul>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition group-hover:text-white">
                        Ver detalle <ChevronRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </PremiumCard>
                </Link>
              </ScrollReveal>
              ))}
            </div>
          )}
        </div>
        </div>
      </section>
    </div>
  );
}
